import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Puzzle, ArrowRight } from 'lucide-react';
import PuzzlePreviewBoard from '../shared/PuzzlePreviewBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import { DEMO_INT_PUZZLE } from '../../data/demo-puzzle';
import './LandingPuzzle.css';

interface LandingPuzzleProps {
  /** Open the fully client-side demo (solvable by everyone, no auth). */
  onSolve?: () => void;
}

/**
 * Landing demo puzzle. Shows the fixed, actually-solvable `demo_int` position
 * (International 10×10, White to play and win) to every visitor. The board and
 * the whole solve flow are validated in-browser (see data/demo-puzzle.ts), so
 * ANY visitor — logged in or not — can solve it; only the real daily puzzle is
 * gated behind registration.
 */

// Read from the single source of truth so the preview can never drift from the
// position the visitor actually solves.
const DEMO_FEN = DEMO_INT_PUZZLE.fen;

export default function LandingPuzzle({ onSolve }: LandingPuzzleProps) {
  const { t } = useTranslation();
  // The preview board is a static image. A visitor may tap it expecting to move
  // the pieces — when they do, pulse the "Risolvi" CTA so they know where to go.
  const [ctaPulse, setCtaPulse] = useState(false);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (pulseTimer.current) clearTimeout(pulseTimer.current); }, []);
  const highlightSolveCta = () => {
    setCtaPulse(true);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setCtaPulse(false), 1200);
  };

  return (
    <section className="lp-section">
      <div className="lp-container">
        <div className="lp-visual">
          {/* Il div esiste per il TOCCO, non per la cornice: `CorniceDamiera` non ha
              una prop `onClick`, e non gliene aggiungiamo una per un host solo. La
              cornice vera e' quella dentro — la stessa di ogni altra damiera. */}
          <div
            className="lp-board-tap"
            onClick={onSolve ? highlightSolveCta : undefined}
          >
            <CorniceDamiera>
              <PuzzlePreviewBoard
                fen={DEMO_FEN}
                variant="international"
                className="lp-board"
                darkSquareClass="sq-dark"
                lightSquareClass="sq-light"
                ariaLabel={t('puzzle.demo.badge', 'Demo puzzle')}
              />
            </CorniceDamiera>
          </div>
          <span className="lp-board-caption">International 10×10</span>
        </div>

        <div className="lp-info">
          <span className="section-label">
            <Puzzle size={13} /> {t('puzzle.demo.badge', 'Demo puzzle')}
          </span>
          <h2 className="section-title">{t('home.landing.puzzleTitle')}</h2>
          <p className="lp-desc">{t('home.landing.puzzleSubtitle')}</p>

          {onSolve && (
            <button
              className={`lp-cta${ctaPulse ? ' lp-cta--pulse' : ''}`}
              onClick={() => onSolve()}
            >
              {t('puzzle.solve')} <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
